import React, { useState, useEffect } from "react";
import { BookOpen, Calendar, CheckCircle, Edit, Trash2, XCircle, FileText, Radio } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import EntityFormModal from "modules/swiftboard/components/EntityFormModal";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import LocalDateTime from "services/date-converter";
import { useAlert } from "context/AlertContext";
import { useTheme } from "context/ThemeContext";
import { getAccentColor } from "utils/accent-color";
import apiKnowledgeBase from "./api";
import { knowledgeBaseFields } from "./features/knowledge-base.fields";
import { knowledgeBaseValidation } from "./features/knowledge-base.validation";
import { loadKnowledgeBaseDependencies } from "./features/knowledge-base.dependencies";
import { knowledgeBaseFilterFields, loadKnowledgeBaseFilterDependencies } from "./features/knowledge-base.filter";

const EMPTY_FILTERS = {};

const KnowledgeBase = ({ onTotalCountChange }) => {
  const { showAlert } = useAlert();
  const { isDark } = useTheme();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [resetSelectionTrigger, setResetSelectionTrigger] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [confirmData, setConfirmData] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Yes",
    cancelText: "No",
    icon: null,
    resolve: null,
  });

  const fetchKnowledgeBases = async () => {
    setLoading(true);
    try {
      const response = await apiKnowledgeBase.getAll({
        searchText,
        search: JSON.stringify(filters),
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
      });

      if (response.data.status) {
        setData(response.data.data);
        setTotalCount(response.data.total_count || 0);
        onTotalCountChange?.(response.data.total_count || 0);
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({ type: "error", message: "An unexpected error occurred. Please try again!" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKnowledgeBases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchText, filters, resetTrigger]);

  const openConfirm = (options) =>
    new Promise((resolve) => {
      setConfirmData({ ...options, isOpen: true, resolve });
    });

  const handleConfirmClose = (result) => {
    confirmData.resolve?.(result);
    setConfirmData((prev) => ({ ...prev, isOpen: false, resolve: null }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const hasActiveQuery = !!searchText || Object.keys(filters).length > 0;

  const handleApplyFilters = ({ search }) => {
    setFilters(search);
    setCurrentPage(1);
  };

  const handlePageChange = (page, newPageSize = pageSize) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const handleRefresh = () => {
    setSearchText("");
    setFilters(EMPTY_FILTERS);
    setCurrentPage(1);
    // Bumping resetTrigger refetches through the effect, after the cleared
    // search and filters are in state — a direct fetch here would still send the old ones.
    setResetTrigger((prev) => prev + 1);
  };

  const handleCreate = () => {
    setSelectedKnowledgeBase(null);
    setIsModalOpen(true);
  };

  const handleEdit = (row) => {
    setSelectedKnowledgeBase(row);
    setIsModalOpen(true);
  };

  const handleClearSelection = () => {
    setSelectedRows([]);
    setResetSelectionTrigger((prev) => prev + 1);
  };

  const handleDeleteSelected = async (ids = []) => {
    if (ids.length === 0) {
      showAlert({ type: "warning", message: "Please select at least one knowledge base to delete." });
      return;
    }
    const confirmed = await openConfirm({
      title: "Delete Knowledge Base",
      message:
        ids.length > 1
          ? `Are you sure you want to delete ${ids.length} selected knowledge bases? This action cannot be undone.`
          : "Are you sure you want to delete this knowledge base? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: <Trash2 size={24} className="text-danger" />,
    });

    if (!confirmed) return;
    try {
      const response = await apiKnowledgeBase.delete(ids);
      if (response.data.status) {
        showAlert({ type: "success", message: response.data?.message });
        fetchKnowledgeBases();
        handleClearSelection();
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({
        type: "error",
        message: error.response?.data?.message || "An unexpected error occurred. Please try again!",
      });
    }
  };

  const handleToggleStatus = async (row) => {
    const confirmed = await openConfirm({
      title: `${row.is_activated ? "Deactivate" : "Activate"} Knowledge Base`,
      message: `Are you sure you want to ${row.is_activated ? "deactivate" : "activate"} "${row.title}"?`,
      confirmText: "Save Changes",
      cancelText: "Cancel",
      icon: <CheckCircle size={24} className="text-success" />,
    });

    if (!confirmed) return;
    const response = await apiKnowledgeBase.update(row.id, { is_activated: !row.is_activated });
    if (response.data.status) {
      showAlert({
        type: "success",
        message: `Knowledge base ${row.is_activated ? "deactivated" : "activated"} successfully`,
      });
      fetchKnowledgeBases();
    }
  };

  const handleModalSuccess = () => {
    fetchKnowledgeBases();
    setSelectedRows([]);
  };

  const columns = [
    {
      key: "order",
      label: "Order",
      width: "80px",
      render: (row) => (
        <span className="inline-flex items-center justify-center min-w-[28px] px-1.5 py-1 rounded-md text-xs font-semibold bg-surface_alt text-text_secondary">
          {row.order ?? "—"}
        </span>
      ),
      sortable: true,
    },
    {
      key: "title",
      label: "Knowledge Base",
      width: "300px",
      render: (row) => {
        // Same accent tokens the reader-facing knowledge base uses, so a
        // chapter's colour looks identical here and in the hub — and a pale
        // colour_code stays legible instead of washing out.
        const accent = row.color_code ? getAccentColor(row.color_code, isDark) : null;
        return (
          <div className="flex items-center gap-2.5">
            {row.banner_image ? (
              <img
                src={row.banner_image}
                alt={row.title}
                className="flex-shrink-0 w-8 h-8 rounded-lg object-cover border border-border"
              />
            ) : (
              <div
                className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary_tint flex items-center justify-center"
                style={accent ? { backgroundColor: accent.fill } : undefined}
              >
                <BookOpen size={15} className="text-primary" style={accent ? { color: accent.text } : undefined} />
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                {accent && (
                  <span
                    className="flex-shrink-0 w-2 h-2 rounded-full border border-border"
                    style={{ backgroundColor: accent.base }}
                    title={row.color_code}
                  />
                )}
                <span className="font-medium text-text_primary truncate">{row.title}</span>
              </div>
              {row.description && (
                <p className="text-[11px] text-text_muted truncate max-w-[220px]">{row.description}</p>
              )}
            </div>
          </div>
        );
      },
      sortable: true,
    },
    {
      key: "user_roles",
      label: "Visible To Roles",
      width: "200px",
      render: (row) =>
        row.user_roles?.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {row.user_roles.map((r) => (
              <span
                key={r.id}
                className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-primary_tint text-primary"
              >
                {r.role}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-text_muted">—</span>
        ),
    },
    {
      key: "trade_of_works",
      label: "Trade of Work",
      width: "260px",
      render: (row) =>
        row.trade_of_works?.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {row.trade_of_works.map((t) => (
              <span
                key={t.id}
                className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-info_bg text-info border border-info_border"
              >
                {t.trade_name}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-text_muted">—</span>
        ),
    },
    {
      key: "business_types",
      label: "Business Type",
      width: "200px",
      render: (row) =>
        row.business_types?.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {row.business_types.map((b) => (
              <span
                key={b.id}
                className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-surface_alt text-text_secondary border border-border"
              >
                {b.name}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-text_muted">—</span>
        ),
    },
    {
      key: "total_articles",
      label: "Total Articles",
      width: "130px",
      render: (row) => (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-surface_alt text-text_secondary">
          <FileText size={12} />
          {row.total_articles ?? 0}
        </span>
      ),
    },
    {
      key: "live_articles",
      label: "Live Articles",
      width: "130px",
      render: (row) => (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-success_bg text-success border border-success_border">
          <Radio size={12} />
          {row.live_articles ?? 0}
        </span>
      ),
    },
    {
      key: "is_activated",
      label: "Status",
      width: "120px",
      render: (row) => (
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold ${
            row.is_activated
              ? "bg-success_bg text-success border border-success_border"
              : "bg-danger_bg text-danger border border-danger_border"
          }`}
        >
          {row.is_activated ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "updated_date",
      label: "Last Update",
      width: "160px",
      render: (row) => (
        <div className="flex items-center gap-1.5 text-xs text-text_primary">
          <Calendar size={12} className="text-text_muted" />
          <LocalDateTime utcTime={row.updated_date} type="datetime" />
        </div>
      ),
      sortable: true,
    },
  ];

  const renderRowActions = (row) => (
    <div className="flex items-center gap-1.5 justify-end">
      <button
        className="p-1.5 rounded-lg bg-info_bg text-info"
        onClick={(e) => {
          e.stopPropagation();
          handleEdit(row);
        }}
        title="Edit Knowledge Base"
      >
        <Edit size={16} />
      </button>
      <button
        className="p-1.5 rounded-lg bg-danger_bg text-danger"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteSelected([row.id]);
        }}
        title="Delete Knowledge Base"
      >
        <Trash2 size={16} />
      </button>
      <button
        className={`p-1.5 rounded-lg ${
          row.is_activated ? "bg-danger_bg text-danger" : "bg-success_bg text-success"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          handleToggleStatus(row);
        }}
        title={row.is_activated ? "Active - Click to Deactivate" : "Inactive - Click to Activate"}
      >
        {row.is_activated ? <XCircle size={18} strokeWidth={2.5} /> : <CheckCircle size={18} strokeWidth={2.5} />}
      </button>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto bg-surface_alt">
      <div className="flex flex-col h-full gap-4">
        <TableToolBar
          onSearch={handleSearch}
          onRefresh={handleRefresh}
          onCreate={handleCreate}
          onDeleteSelected={() => handleDeleteSelected(selectedRows)}
          selectedCount={selectedRows.length}
          resetSearchTrigger={resetTrigger}
          resultCount={totalCount}
          onClearSelection={handleClearSelection}
          advanceFilter={
            <GlobalAdvanceFilter
              fields={knowledgeBaseFilterFields}
              dependenciesLoader={loadKnowledgeBaseFilterDependencies}
              onApply={handleApplyFilters}
              resetKey={resetTrigger}
            />
          }
        />

        {data?.length === 0 && !loading ? (
          <div className="flex-1 flex items-center justify-center border rounded-lg border-border bg-surface">
            <NoDataState
              icon="folder"
              title="No Knowledge Bases Found"
              message={
                hasActiveQuery
                  ? "No knowledge bases match your search or filters."
                  : "There is no knowledge base available yet."
              }
              showRefresh={false}
              showBack={false}
              showCreate={true}
              createText="Create New Knowledge Base"
              onCreate={handleCreate}
              size="medium"
            />
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-hidden border rounded-lg border-border">
              <TableGrid
                data={data}
                totalCount={totalCount}
                loading={loading}
                currentPage={currentPage}
                columns={columns}
                onSelectionChange={setSelectedRows}
                pageSize={pageSize}
                selectable={true}
                renderActions={renderRowActions}
                resetSelectionTrigger={resetSelectionTrigger}
              />
            </div>

            <Pagination
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />

            {confirmData.isOpen && (
              <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />
            )}
          </>
        )}
      </div>

      <EntityFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        id={selectedKnowledgeBase?.id}
        title="Knowledge Base"
        fetchById={apiKnowledgeBase.getById}
        createFn={apiKnowledgeBase.create}
        updateFn={apiKnowledgeBase.update}
        fields={knowledgeBaseFields}
        validationSchema={knowledgeBaseValidation}
        dependenciesLoader={loadKnowledgeBaseDependencies}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default KnowledgeBase;
